import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MeResponse } from '@shared/types';
import { api } from '@/lib/api';
import { clearSession, detailsGiven, loadMe, loadSession, markDetailsAsked, markDetailsGiven, resultPath } from '@/lib/session';
import { isSeatMoved } from '@/lib/seat';
import { useProof } from '@/lib/useProof';
import { useGame } from '@/store/game';
import { ProofMark } from '@/components/ProofMark';
import { Wordmark } from '@/brand/Wordmark';
import { useBattleLook } from '@/look';
import type { ResumeState } from './parts/moments';
import { Screen } from './parts/Screen';
import { FINISH, asksForDetails, finishOf, type ResultArrival } from './parts/finish';
import { DetailsForm, EMPTY_DETAILS, detailsError, saveDetails, useDrawWindow, withSaved, type DetailsValues } from './parts/DetailsForm';

/**
 * 9 Oct: the end of the battle, between the podium and the result. Shown once (lib/session markDetailsAsked) to a player
 * the battle asks who has nothing saved (parts/finish asksForDetails): the battle's heading and message, the details
 * form with the battle's button, and a quiet Not now. Either way the player goes on to the result, whose review is open
 * for everyone; after a save it says the details are saved. A player with something saved, or a battle that asks for
 * nothing, passes straight through, also after signing in here: a known address sees the person icon, and the proof
 * (lib/useProof) reads /me again. Opened with this device's session (the game's, or the one in the address).
 */
export default function Details() {
  const nav = useNavigate();
  // The session at the start: the game's, or the one in the address or on the device. Kept for the page's life, so
  // forgetting it when the seat moves never sends the page anywhere but the join page.
  const [session] = useState(() => useGame.getState().session ?? loadSession());
  const [me, setMe] = useState<MeResponse>();
  const [values, setValues] = useState<DetailsValues>(EMPTY_DETAILS);
  const [err, setErr] = useState<string>(); const [busy, setBusy] = useState(false);
  const draw = useDrawWindow(me?.state);
  useBattleLook(me?.state);

  /** On to the result: on the read-only link /me handed over (R1), else on this device's session. */
  const toResult = (m: MeResponse | undefined, arrival?: ResultArrival) =>
    nav(m?.resultToken ? resultPath(m.resultToken) : '/r', { replace: true, ...(arrival ? { state: arrival } : {}) });
  // Another device took this seat (lib/seat): the session is forgotten and the battle's join page says so.
  const seatGone = (): boolean => {
    clearSession(); const g = useGame.getState(); g.setSession(undefined); g.clearSeatMoved();
    if (!session) return false;
    nav(`/j/${session.code}`, { replace: true, state: { resume: 'moved', name: session.name } satisfies ResumeState });
    return true;
  };
  const moved = useGame((s) => s.seatMoved);
  useEffect(() => { if (moved) seatGone(); }, [moved]); // eslint-disable-line react-hooks/exhaustive-deps
  const take = (m: MeResponse) => {
    if (!asksForDetails(m) || detailsGiven(loadMe()?.email)) { toResult(m); return; }
    setMe(m); setValues((v) => withSaved(v, m.details));
  };

  useEffect(() => {
    // No session on this device: the result page says there is nothing to show.
    if (!session) { nav('/r', { replace: true }); return; }
    markDetailsAsked(session.code, session.playerId);
    let live = true;
    api<MeResponse>('/me', { token: session.token }).then((m) => { if (live) take(m); }).catch((e) => {
      if (!live) return;
      if (isSeatMoved(e) && seatGone()) return;
      // The step is never in the way: without /me, on to the result, which says what went wrong.
      toResult(undefined);
    });
    return () => { live = false; };
  }, [session?.token]); // eslint-disable-line react-hooks/exhaustive-deps

  useProof({ session, me, onProved: take });

  async function save() {
    if (!session || !me) return;
    setBusy(true); setErr(undefined);
    try {
      const saved = await saveDetails(session.token, values, { draw });
      markDetailsGiven(loadMe()?.email);
      toResult(me, { saved });
    } catch (e) {
      if (isSeatMoved(e) && seatGone()) return;
      setErr(detailsError(e)); setBusy(false);
    }
  }

  if (!me) return <Screen center><p className="text-center font-semibold text-ink-2">{FINISH.loading}</p></Screen>;
  const finish = finishOf(me);
  return (
    <Screen className="gap-6 pb-12">
      <header className="flex flex-col gap-4 pt-4">
        <div className="flex items-center justify-between gap-3">
          <Wordmark variant="compact" />
          <ProofMark where="details" me={me} session={session} />
        </div>
        <div className="flex flex-col gap-1">
          {me.state.title?.trim() && <p className="font-semibold text-ink-2">{me.state.title}</p>}
          <h1 id="finish-heading" className="display text-[2.4rem] leading-tight text-ink">{finish.heading}</h1>
          <p className="mt-1 text-ink-2">{finish.message}</p>
          <p className="text-sm text-ink-3">{FINISH.emailNote}</p>
        </div>
      </header>
      <DetailsForm labelledBy="finish-heading" values={values} onChange={setValues} draw={draw} code={me.state.code}
        err={err} busy={busy} submitLabel={finish.button} onSubmit={() => void save()} />
      <button type="button" data-part="not-now" onClick={() => toResult(me)}
        className="inline-flex min-h-11 items-center self-center px-1 font-semibold text-iv-strong underline underline-offset-2">{FINISH.notNow}</button>
    </Screen>
  );
}
